/**
 * KisiDuzenlemeTalepleriPage.jsx — Edit Kişi Requests, the staff approval list (backlog #749; backend #747, KisiDuzenlemeTalebiController).
 * Route: /Management/Kisi/DuzenlemeTalepleri (permission page.kisi). Reached from Kişi Listesi (button with the pending count) and from a kişi's
 * own page (the notice, `?personId=`). No sidebar item: "Kişi" stays one link (#385).
 *
 * A company login that changes a worker's phone / e-mail on a LOCKED card (KVKK accepted, Kimlik Doğrulandı, or a verified phone) does not write:
 * its change waits here. Per request: the kişi, the company and who asked, the old → new value next to what the card holds NOW
 * (`guncelTelefon` / `guncelEposta`; shown in amber when the card moved on since the request was made), and why it was locked (`kilitSebepleri`).
 * Onayla writes it through the künye editor as the approver (a refusal of the editor is shown and the request stays pending); Reddet asks for the
 * reason the company will read (required, 500 characters). The view lives in the address bar: `durum` (absent = pending, "tum" = every state,
 * 0-3), `personId`, `sayfa`.
 */
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert, Badge, Button, Card, CardBody, Container, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader,
  Pagination, PaginationItem, PaginationLink, Spinner, Table,
} from "reactstrap";
import { Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { NEUTRAL_GRAY, STATUS_HEX } from "../../../Components/Projects/StandPills";
import { tarihSaat } from "../../../Components/Kullanici/KullaniciParcalari";
import { kilitMetni } from "../../../Components/Kisi/duzenlemeTalebi";
import { kisiSayfasiUrl } from "../../../Components/Kisi/kisiBaglantilari";
import {
  getDuzenlemeTalepleri, onaylaTalep, reddetTalep, talepHatasi, TALEP_DURUMU,
} from "../../../services/kisiDuzenlemeTalebiService";
import i18n, { currentLocaleTag } from "../../../i18n";

const SAYFA_BOYU = 25;
const TUMU = "tum";
const RED_SEBEBI_EN_FAZLA = 500;
const DURUM_ANAHTARI = ["bekliyor", "onaylandi", "reddedildi", "geriCekildi"];
const DURUM_RENK = {
  [TALEP_DURUMU.BEKLIYOR]: STATUS_HEX.warning,
  [TALEP_DURUMU.ONAYLANDI]: STATUS_HEX.success,
  [TALEP_DURUMU.REDDEDILDI]: STATUS_HEX.danger,
  [TALEP_DURUMU.GERI_CEKILDI]: NEUTRAL_GRAY,
};

function RedModal({ talep, onClose }) {
  const { t } = useTranslation("yonetim");
  const [sebep, setSebep] = useState("");
  const [saving, setSaving] = useState(false);
  const [hata, setHata] = useState(null);
  const kapat = () => { if (!saving) onClose(null); };

  async function reddet() {
    setHata(null);
    if (!sebep.trim()) { setHata(t("kisiDuzenlemeTalepleri.redSebebiZorunlu")); return; }
    setSaving(true);
    try {
      onClose(await reddetTalep(talep.id, sebep.trim()));
    } catch (e) {
      setHata(talepHatasi(e) || t("kisiDuzenlemeTalepleri.reddedilemedi"));
      setSaving(false);
    }
  }

  return (
    <Modal isOpen toggle={kapat}>
      <ModalHeader toggle={kapat}>{t("kisiDuzenlemeTalepleri.redBaslik")}</ModalHeader>
      <ModalBody>
        {hata && <Alert fade={false} color="danger" className="py-2 small">{hata}</Alert>}
        <p className="small text-muted">
          {t("kisiDuzenlemeTalepleri.redAciklama", { kisi: `${talep.personAd || ""} ${talep.personSoyad || ""}`.trim(), firma: talep.firmaAdi || `#${talep.firmaId}` })}
        </p>
        <FormGroup className="mb-0">
          <Label for="kdt-red-sebebi">{t("kisiDuzenlemeTalepleri.redSebebi")} *</Label>
          <Input
            id="kdt-red-sebebi" type="textarea" rows={4} value={sebep} maxLength={RED_SEBEBI_EN_FAZLA} disabled={saving}
            onChange={(e) => setSebep(e.target.value)}
          />
          <div className="form-text text-end">{sebep.length} / {RED_SEBEBI_EN_FAZLA}</div>
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={kapat} disabled={saving}>{t("common:iptal")}</Button>
        <Button color="danger" onClick={reddet} disabled={saving} data-testid="kdt-reddet-gonder">
          {saving ? <Spinner size="sm" /> : t("kisiDuzenlemeTalepleri.reddet")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/** One changed field: old → new, and the value the card holds now (amber when it is not the old one any more). */
function Degisim({ etiket, eski, yeni, guncel }) {
  const { t } = useTranslation("yonetim");
  const kaymis = (guncel || "") !== (eski || "");
  return (
    <div className="mb-1">
      <div className="text-muted small">{etiket}</div>
      <div className="text-break">
        <span className="text-muted">{eski || "—"}</span> <span className="text-muted">→</span> <span className="fw-semibold">{yeni || "—"}</span>
      </div>
      <div className={`small ${kaymis ? "text-warning" : "text-muted"}`}>{t("kisiDuzenlemeTalepleri.kartta", { deger: guncel || "—" })}</div>
    </div>
  );
}

export default function KisiDuzenlemeTalepleriPage() {
  const { t } = useTranslation("yonetim");
  const { t: tc } = useTranslation("components");
  const [sp, setSp] = useSearchParams();

  // The whole view comes from the address bar. durum: a state number, "tum" = all states, absent = pending.
  const durumParam = sp.get("durum");
  const durum = durumParam === TUMU ? TUMU : (["0", "1", "2", "3"].includes(durumParam) ? durumParam : String(TALEP_DURUMU.BEKLIYOR));
  const personId = Number(sp.get("personId")) || null;
  const sayfa = Math.max(1, Number(sp.get("sayfa")) || 1);

  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);           // an action (Onayla) refused
  const [okumaHatasi, setOkumaHatasi] = useState(null); // the list could not be read
  const [bilgi, setBilgi] = useState(null);
  const [yenile, setYenile] = useState(0);
  const [calisan, setCalisan] = useState(null);   // request id being approved
  const [reddedilen, setReddedilen] = useState(null);

  useEffect(() => { document.title = i18n.t("yonetim:kisiDuzenlemeTalepleri.sayfaBasligi"); }, []);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setYukleniyor(true);
      setOkumaHatasi(null);
      try {
        const d = await getDuzenlemeTalepleri({ durum: durum === TUMU ? null : Number(durum), personId, page: sayfa - 1, size: SAYFA_BOYU });
        if (!ignore) setVeri(d);
      } catch (e) {
        if (!ignore) { setOkumaHatasi(talepHatasi(e) || i18n.t("yonetim:kisiDuzenlemeTalepleri.okunamadi")); setVeri(null); }
      } finally {
        if (!ignore) setYukleniyor(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [durum, personId, sayfa, yenile]);

  function guncelle(patch) {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); });
    if (!("sayfa" in patch)) next.delete("sayfa");
    setSp(next, { replace: true });
  }

  async function onayla(r) {
    setCalisan(r.id);
    setHata(null);
    setBilgi(null);
    try {
      const sonuc = await onaylaTalep(r.id);
      setBilgi(sonuc.mesaj || t("kisiDuzenlemeTalepleri.onaylandi"));
    } catch (e) {
      setHata(talepHatasi(e) || t("kisiDuzenlemeTalepleri.onaylanamadi")); // the künye editor's refusal: the request stays pending
    } finally {
      setCalisan(null);
      setYenile((x) => x + 1);
    }
  }

  const talepler = veri?.talepler || [];
  const toplam = veri?.toplam ?? 0;
  const toplamSayfa = Math.max(1, Math.ceil(toplam / SAYFA_BOYU));
  const bekleyen = veri?.bekleyenSayisi ?? 0;
  const kisiAdi = talepler[0] ? `${talepler[0].personAd || ""} ${talepler[0].personSoyad || ""}`.trim() : "";

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("kisiDuzenlemeTalepleri.baslik")} pageTitle={t("kisiDuzenlemeTalepleri.kisiler")} />

        <Card className="mb-3">
          <CardBody className="d-flex align-items-center flex-wrap gap-2" data-testid="kdt-durumlar">
            <Button
              color={durum === TUMU ? "primary" : "light"} size="sm" onClick={() => guncelle({ durum: TUMU })} data-testid="kdt-durum-tum"
            >
              {t("kisiDuzenlemeTalepleri.tumu")}
            </Button>
            {DURUM_ANAHTARI.map((anahtar, i) => (
              <Button
                key={anahtar} color={durum === String(i) ? "primary" : "light"} size="sm" data-testid={`kdt-durum-${i}`}
                onClick={() => guncelle({ durum: i === TALEP_DURUMU.BEKLIYOR ? "" : String(i) })}
              >
                {t(`kisiDuzenlemeTalepleri.durum.${anahtar}`)}
                {i === TALEP_DURUMU.BEKLIYOR && bekleyen > 0 && <Badge color="warning" pill className="ms-2" data-testid="kdt-bekleyen">{bekleyen}</Badge>}
              </Button>
            ))}
            {personId && (
              <Button color="light" size="sm" className="ms-auto" onClick={() => guncelle({ personId: "" })} data-testid="kdt-kisi-suzgeci">
                {t("kisiDuzenlemeTalepleri.kisiSuzgeci", { kisi: kisiAdi || `#${personId}` })} <X size={13} className="ms-1" />
              </Button>
            )}
          </CardBody>
        </Card>

        {bilgi && <Alert fade={false} color="success" toggle={() => setBilgi(null)}>{bilgi}</Alert>}
        {hata && <Alert fade={false} color="danger" toggle={() => setHata(null)} data-testid="kdt-hata">{hata}</Alert>}
        {okumaHatasi && <Alert fade={false} color="danger">{okumaHatasi}</Alert>}

        {yukleniyor && !veri && <div className="text-center py-5"><Spinner color="primary" /></div>}

        {veri && (
          <Card style={yukleniyor ? { opacity: 0.5 } : undefined}>
            <CardBody className="p-0">
              {talepler.length === 0 ? (
                <div className="text-center text-muted py-5" data-testid="kdt-bos">{t("kisiDuzenlemeTalepleri.bos")}</div>
              ) : (
                <div className="table-responsive">
                  <Table className="align-middle mb-0" data-testid="kdt-tablo">
                    <thead className="table-light text-muted small text-uppercase">
                      <tr>
                        <th>{t("kisiDuzenlemeTalepleri.kisi")}</th>
                        <th>{t("kisiDuzenlemeTalepleri.firma")}</th>
                        <th>{t("kisiDuzenlemeTalepleri.degisiklik")}</th>
                        <th>{t("kisiDuzenlemeTalepleri.nedenOnay")}</th>
                        <th>{t("kisiDuzenlemeTalepleri.durumBasligi")}</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {talepler.map((r) => (
                        <tr key={r.id} data-talep={r.id}>
                          <td>
                            <Link to={kisiSayfasiUrl({ personId: r.personId })} className="fw-semibold">
                              {`${r.personAd || ""} ${r.personSoyad || ""}`.trim() || `#${r.personId}`}
                            </Link>
                          </td>
                          <td className="small">
                            <Link to={`/Management/Firmalar/Detay/${r.firmaId}`}>{r.firmaAdi || `#${r.firmaId}`}</Link>
                            <div className="text-muted">{r.talepEdenAdi || "—"}</div>
                            <div className="text-muted text-nowrap">{tarihSaat(r.olusturmaTarihi)}</div>
                          </td>
                          <td className="small">
                            {r.telefonDegisir && <Degisim etiket={tc("duzenlemeTalebi.telefon")} eski={r.eskiTelefon} yeni={r.yeniTelefon} guncel={r.guncelTelefon} />}
                            {r.epostaDegisir && <Degisim etiket={tc("duzenlemeTalebi.eposta")} eski={r.eskiEposta} yeni={r.yeniEposta} guncel={r.guncelEposta} />}
                          </td>
                          <td className="small" data-testid={`kdt-sebep-${r.id}`}>{kilitMetni(tc, r.kilitSebepleri) || "—"}</td>
                          <td className="small">
                            <span className="badge text-white fw-normal" style={{ backgroundColor: DURUM_RENK[r.durum] ?? NEUTRAL_GRAY }}>
                              {t(`kisiDuzenlemeTalepleri.durum.${DURUM_ANAHTARI[r.durum] ?? "bekliyor"}`)}
                            </span>
                            {r.durum !== TALEP_DURUMU.BEKLIYOR && r.durum !== TALEP_DURUMU.GERI_CEKILDI && (
                              <div className="text-muted mt-1">{r.kararVerenAdi || ""}{r.kararTarihi ? ` · ${tarihSaat(r.kararTarihi)}` : ""}</div>
                            )}
                            {r.durum === TALEP_DURUMU.REDDEDILDI && r.redSebebi && (
                              <div className="text-danger">{t("kisiDuzenlemeTalepleri.redSebebi")}: {r.redSebebi}</div>
                            )}
                          </td>
                          <td className="text-end text-nowrap">
                            {r.durum === TALEP_DURUMU.BEKLIYOR && (
                              <>
                                <Button color="success" size="sm" className="me-1" disabled={calisan === r.id} onClick={() => onayla(r)} data-testid={`kdt-onayla-${r.id}`}>
                                  {calisan === r.id ? <Spinner size="sm" /> : <><Check size={14} className="me-1" />{t("kisiDuzenlemeTalepleri.onayla")}</>}
                                </Button>
                                <Button color="danger" outline size="sm" disabled={calisan === r.id} onClick={() => setReddedilen(r)} data-testid={`kdt-reddet-${r.id}`}>
                                  <X size={14} className="me-1" />{t("kisiDuzenlemeTalepleri.reddet")}
                                </Button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        )}

        {toplamSayfa > 1 && (
          <div className="d-flex flex-column align-items-center p-3">
            <Pagination>
              <PaginationItem disabled={sayfa <= 1}><PaginationLink previous onClick={() => guncelle({ sayfa: String(sayfa - 1) })} /></PaginationItem>
              <PaginationItem active><PaginationLink>{sayfa}</PaginationLink></PaginationItem>
              <PaginationItem disabled={sayfa >= toplamSayfa}><PaginationLink next onClick={() => guncelle({ sayfa: String(sayfa + 1) })} /></PaginationItem>
            </Pagination>
            <div className="text-muted small">
              {t("kisiDuzenlemeTalepleri.sayfaBilgisi", { sayfa, toplamSayfa: toplamSayfa.toLocaleString(currentLocaleTag()), toplam: toplam.toLocaleString(currentLocaleTag()) })}
            </div>
          </div>
        )}

        {reddedilen && (
          <RedModal
            talep={reddedilen}
            onClose={(sonuc) => {
              setReddedilen(null);
              if (!sonuc) return;
              setBilgi(sonuc.mesaj || t("kisiDuzenlemeTalepleri.reddedildi"));
              setYenile((x) => x + 1);
            }}
          />
        )}
      </Container>
    </div>
  );
}
